import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Avatar, Button, Card, Input, Message, Spin, Tag, Typography } from "@arco-design/web-react";
import styled from "styled-components";
import { FetchApi, SaveApi } from "../Api/AuthenticatedApi";
import { useUnsavedGuard } from "../utils/useUnsavedGuard";
import { avatarInitial } from "../AppBarAndDrawer/avatarInitial";
import Content from "../Layout/Content";
import { setMyUser } from "./myUser";

const { Title, Text } = Typography;

const Field = styled.div`
  margin-bottom: 20px;
  max-width: 420px;
`;

const FieldLabel = styled.div`
  font-size: 12px;
  color: var(--color-text-3);
  margin-bottom: 4px;
`;

const Hint = styled.div`
  font-size: 12px;
  color: var(--color-text-3);
  margin-top: 4px;
`;

// Your account: the signed-in person, not the organization (that is Settings).
// The name can be changed here. The email is how you sign in, so it is shown
// but managed by the login provider.
export default function Account() {
  const { user: authUser, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [me, setMe] = React.useState(null);
  const [name, setName] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  useEffect(() => {
    FetchApi(getAccessTokenSilently, "/api/me",
      (data) => {
        setMe(data);
        setName((data && data.user && data.user.name) || "");
      },
      () => navigate("/login"),
      () => Message.error("Failed to load your account"));
  }, [getAccessTokenSilently, navigate]);

  const savedName = (me && me.user && me.user.name) || "";
  const isDirty = name !== savedName;
  useUnsavedGuard(isDirty);

  if (!me) {
    return (
      <Content>
        <Spin />
      </Content>
    );
  }

  const user = me.user || {};
  const orgs = me.organizations || [];
  const canSave = isDirty && name.trim() !== "" && !saving;

  const save = () => {
    setSaving(true);
    SaveApi(getAccessTokenSilently, "/api/me",
      (updated) => {
        setSaving(false);
        setMe((prev) => ({ ...prev, user: updated }));
        setName(updated.name);
        setMyUser(updated);
        Message.success("Name saved");
      },
      () => navigate("/login"),
      (err) => {
        setSaving(false);
        Message.error((err && err.message) || "Failed to save your name");
      },
      { name });
  };

  return (
    <Content>
      <Title heading={4} style={{ marginTop: 0 }}>Your account</Title>
      <Card style={{ maxWidth: 640 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
          <Avatar size={56} style={{ backgroundColor: "rgb(var(--arcoblue-6))" }}>
            {authUser && authUser.picture
              ? <img src={authUser.picture} alt="" />
              : avatarInitial(user)}
          </Avatar>
          <div>
            <Text bold style={{ fontSize: 16, display: "block" }}>{user.name || user.email}</Text>
            <Text type="secondary">{user.email}</Text>
          </div>
        </div>

        <Field>
          <FieldLabel>Name</FieldLabel>
          <Input
            id="account-name"
            value={name}
            maxLength={100}
            onChange={setName}
            onPressEnter={() => canSave && save()}
          />
        </Field>

        <Field>
          <FieldLabel>Email</FieldLabel>
          <Input id="account-email" value={user.email || ""} readOnly />
          <Hint>This is the email you sign in with. It can't be changed here.</Hint>
        </Field>

        <Field>
          <FieldLabel>Organizations</FieldLabel>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {orgs.map((o) => (
              <Tag key={o.id} color={o.id === me.active_organization_id ? "arcoblue" : undefined}>
                {o.name}{o.id === me.active_organization_id ? " (current)" : ""}
              </Tag>
            ))}
          </div>
        </Field>

        <div style={{ display: "flex", gap: 8 }}>
          <Button type="primary" onClick={save} disabled={!canSave} loading={saving}>Save</Button>
          {isDirty && <Button onClick={() => setName(savedName)}>Discard</Button>}
        </div>
      </Card>
    </Content>
  );
}
